/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { PageRoute } from '../types';

interface FooterProps {
  setRoute: (route: PageRoute) => void;
  scrollToSection: (id: string) => void;
  showSocials?: boolean;
}

const socials = [
  { label: 'X（Twitter）', mark: 'X', href: 'https://x.com/KoemoriOfficial' },
  { label: 'TikTok', mark: 'TikTok', href: 'https://www.tiktok.com/@koemori_official' },
];

export default function Footer({ setRoute, scrollToSection, showSocials = false }: FooterProps) {
  const handleNavClick = (sectionId: string) => {
    setRoute('home');
    window.setTimeout(() => scrollToSection(sectionId), 80);
  };

  const handleLegalClick = (route: PageRoute) => {
    setRoute(route);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#050606] border-t border-white/[0.06] py-7 lg:flex-1 lg:flex lg:flex-col lg:justify-center lg:py-5 text-white/45 relative overflow-hidden">
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-40 w-[720px] h-64 rounded-full bg-[#00F5D4]/4 blur-[110px]" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-7">
        <div className="lg:col-span-4">
          <button onClick={() => handleNavClick('hero')} className="flex items-center gap-3 cursor-pointer group mb-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/10 shadow-[0_0_18px_rgba(0,245,212,0.1)] group-hover:scale-105 transition-transform">
              <img src="/logo.webp" alt="Koemori Logo" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-black text-white group-hover:text-[#00F5D4] transition-colors">Koemori</span>
          </button>
          <p className="text-sm leading-6 max-w-sm text-white/38">
            音楽で、人々をつなぐ。高品質な伴奏、ボーカル分析と斬新なコラボレーション体験で、音楽を出会いの始まりに。
          </p>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white text-xs font-black uppercase tracking-[0.16em] mb-3">アプリについて</h4>
          <ul className="space-y-2 text-sm">
            <li><button onClick={() => handleNavClick('features')} className="hover:text-[#00F5D4] transition-colors cursor-pointer">アプリ内容</button></li>
            <li><button onClick={() => handleNavClick('download')} className="hover:text-[#00F5D4] transition-colors cursor-pointer">アップデート情報</button></li>
            <li><button onClick={() => handleLegalClick('services')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">ユーザーサービス</button></li>
            <li><button onClick={() => handleLegalClick('song-submission')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">楽曲の投稿</button></li>
            <li><button onClick={() => handleLegalClick('domily')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">Domilyについて</button></li>
            <li><button onClick={() => handleLegalClick('domi')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">Domi</button></li>
            <li><a href="mailto:koemori@meetune.com" className="hover:text-[#00F5D4] transition-colors">ヘルプセンター</a></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white text-xs font-black uppercase tracking-[0.16em] mb-3">法的表示</h4>
          <ul className="space-y-2 text-sm">
            <li><button onClick={() => handleLegalClick('privacy')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">プライバシーポリシー</button></li>
            <li><button onClick={() => handleLegalClick('terms')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">利用規約</button></li>
            <li><button onClick={() => handleLegalClick('commercial')} className="hover:text-[#00F5D4] transition-colors cursor-pointer text-left">特定商取引法に基づく表記</button></li>
          </ul>
        </div>

        {showSocials && <div className="lg:col-span-3">
          <h4 className="text-white text-xs font-black uppercase tracking-[0.16em] mb-3">公式アカウント</h4>
          <div className="flex flex-wrap gap-2.5">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                title={`${social.label} 公式アカウントはサービス開始後に公開予定です`}
                aria-label={social.label}
                className="min-w-10 h-10 px-2.5 rounded-xl border border-white/[0.08] bg-white/[0.035] text-[10px] font-black text-white/55 flex items-center justify-center hover:-translate-y-0.5 hover:text-[#00F5D4] hover:border-[#00F5D4]/30 transition-all"
              >
                {social.mark === 'TikTok' ? (
                  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="#25F4EE" d="M15.7 3.2c.5 1.8 1.6 3.1 3.4 3.6v3.1a8.2 8.2 0 0 1-3.3-1v6.3a5.7 5.7 0 1 1-5.7-5.7c.4 0 .8 0 1.2.1v3.2a2.6 2.6 0 1 0 1.4 2.4V3.2h3z" />
                    <path fill="#FE2C55" d="M14.5 4.3v9.8a5.7 5.7 0 0 1-5.7 5.7 5.6 5.6 0 0 1-2.2-.4 5.7 5.7 0 0 0 9.2-4.5V8.6a8.2 8.2 0 0 0 3.3 1V7.8a5.2 5.2 0 0 1-2.1-.8 5.6 5.6 0 0 1-2.5-2.7z" opacity=".9" />
                    <path fill="#fff" d="M14.1 3.2v9.7a2.6 2.6 0 1 1-2.6-2.6c.4 0 .8.1 1.2.3V7.4a5.7 5.7 0 1 0 4.2 5.5V6.6a5.6 5.6 0 0 1-2.8-3.4z" opacity=".95" />
                  </svg>
                ) : social.mark === 'X' ? (
                  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                ) : social.mark}
              </a>
            ))}
          </div>
          <p className="text-[11px] text-white/25 mt-2">公式アカウントは、サービス開始後に公開する予定です。</p>
        </div>}
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 mt-6 pt-4 border-t border-white/[0.06] text-center text-xs text-white/28 space-y-2">
        <p>© 2026 Meetune Entertainment株式会社. All Rights Reserved.</p>
        <p className="text-[10px] text-white/18 leading-relaxed tracking-wide">
          JASRAC許諾第9042288001Y31015号、第9042288002Y31015号、第9042288003Y30006号 ｜ NexTone許諾番号 ID000012281・ID000012264
        </p>
      </div>
    </footer>
  );
}
